import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import type { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";

import ImageCanvasVisualizer, { DRAG_THRESHOLD_PX } from "./ImageCanvasVisualizer";
import { getVisualizerInstance } from "../docking/dockStore";
import { useCanvasSize } from "./useCanvasSize";
import useResizingLoaderHook from "./useResizingLoaderHook";
import useLaptopPinchIncrease from "./useLaptopPinchIncrease";
import LoadingSpinner from "../ui/loadingSpinner";
import { computeCoverTransform, getWrapperViewportRect } from "./canvasTransformMath";
import { useZoomToActiveFeatureGroup } from "./zoomToFeatureGroup/useZoomToActiveFeatureGroup";
import { ZoomPanel } from "./VisualizerCanvasZoomPanel";
import {useAppSelector} from "../../storeHooks";

const MIN_SCALE_DOCKED = 0.75;
const TRANSITION_MS = 500;
const TRANSITION_BUFFER_MS = 60;

export interface DockRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type DockMode = "fullscreen" | "embedded";

export interface DockedVisualizerWrapperProps {
  input: any;
  dockRect: DockRect | null;
  dockMode: DockMode;
}

// rzpp's onPanningStart fires on raw mousedown/touchstart — before any actual
// movement — so it can't be used directly to flag a drag (it made
// window.canvasWasDragged true on every click). Pull the client point out of
// either a MouseEvent or a TouchEvent so the same movement-threshold check
// works for mouse, trackpad, and touch panning.
function getEventPoint(event: MouseEvent | TouchEvent): { x: number; y: number } {
  const touch =
    ("touches" in event && event.touches && event.touches[0]) ||
    ("changedTouches" in event && event.changedTouches && event.changedTouches[0]);
  const source: any = touch || event;
  return { x: source.clientX, y: source.clientY };
}

/**
 * Persistent wrapper around the single VisualizerAdapter. Ported from v3's
 * VisualizerWrapper: owns the react-zoom-pan-pinch TransformWrapper and continuously
 * fits/centers the canvas to the current dock box (the parent slot), so the one
 * visualizer instance scales to whatever slot it's docked into — without remounting.
 *
 * Props:
 *   input    : { layers, layout, layoutType, canvasId, width, height, useMouseHovering, useTouchZoom }
 *   dockRect : { top, left, width, height } — the active slot's box (from the shell)
 *   dockMode : 'fullscreen' | 'embedded'
 */
export default function DockedVisualizerWrapper({ input, dockRect, dockMode }: DockedVisualizerWrapperProps) {
  const canvasSize = useCanvasSize(input && input.width, input && input.height);
  const { rwKey, isResizing } = useResizingLoaderHook();

  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const getRzpp = useCallback(() => transformRef.current, []);
  const panStartPointRef = useRef<{ x: number; y: number } | null>(null);

  const isTouch = useAppSelector(state => state.serverState.isTouch);

  // True for the ~500ms that syncToViewport's RAF loop (below) is actively driving the transform
  // to follow the dock shell's own animating box. Shared with useZoomToActiveFeatureGroup so it
  // can wait its turn instead of racing that loop's per-frame instant jumps with its own animated
  // setTransform call — see the JSDoc on useZoomToActiveFeatureGroup for why that hand-off matters.
  const isSyncingViewportRef = useRef(false);

  // Speeds up trackpad pinch-to-zoom (ctrl+wheel) without touching ordinary
  // wheel scrolling. Remove this line to fully disable/revert that behavior.
  useLaptopPinchIncrease(transformRef);

  const minScale = useMemo(() => {
    if (dockMode !== "embedded" || !dockRect) return 1;
    const floorByWidth = dockRect.width / canvasSize.width;
    const floorByHeight = dockRect.height / canvasSize.height;
    return Math.max(MIN_SCALE_DOCKED, floorByWidth, floorByHeight);
  }, [dockMode, dockRect, canvasSize.width, canvasSize.height]);

  // Zooms/pans to whichever feature group is active in the currently docked slot
  // (the materials wizard, and the fullscreen visualizer's mobile navigation) — see
  // zoomToFeatureGroup/ for the constants, the math, and the on/off flag.
  useZoomToActiveFeatureGroup(transformRef, canvasSize, minScale, isSyncingViewportRef);

  // The cover-fit transform for the CURRENT dock box, computed synchronously during render so
  // <TransformWrapper> can be *born* framed (initialPositionX/Y below) instead of at
  // react-zoom-pan-pinch's default 0/0.
  //
  // Why this matters on a first load of /materials: rzpp's `createState` seeds positionX/positionY
  // from initialPositionX/initialPositionY (defaulting to 0), calls `applyTransformation()`
  // immediately, and only corrects to centered later — `centerOnInit` runs from the ResizeObserver
  // callback it installs in `handleInitialize`, which fires *after* the commit that mounted it has
  // already painted. With no initial position that first painted frame has the content pinned to
  // the wrapper's top-left corner, i.e. the canvas visibly showing its far-left edge, and it only
  // snaps to centered a frame or two later. Seeding the real values removes that frame entirely.
  //
  // These are the same numbers `centerOnInit`/`setCenter` would arrive at on its own
  // (getCenterPosition = (wrapper - content * scale) / 2, which is exactly what
  // computeCoverTransform's clamp resolves to whenever the scaled content covers the box), so
  // leaving centerOnInit on costs nothing and stays as a safety net — it just no longer has
  // anything visible left to correct.
  const initialTransform = useMemo(() => {
    const viewWidth = dockRect ? dockRect.width : window.innerWidth;
    const viewHeight = dockRect ? dockRect.height : window.innerHeight;
    return computeCoverTransform({
      contentWidth: canvasSize.width,
      contentHeight: canvasSize.height,
      viewportWidth: viewWidth,
      viewportHeight: viewHeight,
      scale: dockMode === "embedded" ? minScale : 1,
    });
  }, [dockMode, dockRect, minScale, canvasSize.width, canvasSize.height]);

  const canvasSizeRef = useRef(canvasSize);
  const dockModeRef = useRef(dockMode);
  useEffect(() => {
    canvasSizeRef.current = canvasSize;
    dockModeRef.current = dockMode;
  }, [canvasSize, dockMode]);

  // Fallback for a pan that ends with the pointer released off this canvas (e.g.
  // releasing right on the "open fullscreen visualizer" button while dragging the
  // small embedded /materials dock) - see the JSDoc on ImageCanvasVisualizer's own
  // resetDragState for why that leaves pointerDown/isDragging stuck true, and
  // window.canvasWasDragged stuck true too (its own consuming click on the canvas
  // never happens for that gesture either). Both then leak into whichever mode we
  // switch to next, so clear both whenever dockMode actually changes.
  useEffect(() => {
    (window as any).canvasWasDragged = false;
    const instance: any = getVisualizerInstance();
    if (instance && typeof instance.resetDragState === "function") {
      instance.resetDragState();
    }
  }, [dockMode]);

  // Re-center/scale against the *live* (interpolating) dock box. The shell animates
  // the overlay via a CSS transition, so its getBoundingClientRect() changes every
  // frame; we derive the scale from that live box so the canvas moves in lockstep.
  const syncToViewport = useCallback(() => {
    const ref = transformRef.current;
    if (!ref) return;
    const { setTransform } = ref;
    const { width: csWidth, height: csHeight } = canvasSizeRef.current;

    const viewportRect = getWrapperViewportRect(ref);
    const viewW = viewportRect ? viewportRect.width : window.innerWidth;
    const viewH = viewportRect ? viewportRect.height : window.innerHeight;

    const scale =
      dockModeRef.current === "embedded"
        ? Math.max(MIN_SCALE_DOCKED, viewW / csWidth, viewH / csHeight)
        : 1;

    const { positionX, positionY } = computeCoverTransform({
      contentWidth: csWidth,
      contentHeight: csHeight,
      viewportWidth: viewW,
      viewportHeight: viewH,
      scale,
    });

    setTransform(positionX, positionY, scale, 0);
  }, []);

  // Fit the canvas to its box BEFORE the browser paints a freshly-mounted <TransformWrapper>.
  //
  // `initialPositionX/Y` above already covers the values known at render time, but the wrapper's
  // real on-screen box is only measurable after layout — and that's what syncToViewport reads. A
  // *layout* effect is what makes this safe: it runs in the same commit, after rzpp's own
  // componentDidMount (children commit first, so `transformRef.current` and its wrapper element
  // both exist) and still before paint. The RAF loop below can't do this job on its own: its first
  // tick is a requestAnimationFrame callback, which lands after the mounting commit has painted —
  // that one already-painted frame is the "canvas flashes at its far-left edge" artifact.
  //
  // Keyed on rwKey/isResizing because those are exactly the two things that unmount and remount
  // the wrapper (see useResizingLoaderHook): a true orientation flip swaps in the spinner and then
  // brings back a brand-new rzpp instance, which starts over from its initial state.
  useLayoutEffect(() => {
    syncToViewport();
  }, [rwKey, isResizing, syncToViewport]);

  // Follow the dock shell's layout animation for its full duration on mode/slot
  // change (and on canvas-size change from a window resize).
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    isSyncingViewportRef.current = true;
    const start = performance.now();
    const tick = (now: number) => {
      syncToViewport();
      if (now - start < TRANSITION_MS + TRANSITION_BUFFER_MS) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        isSyncingViewportRef.current = false;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      isSyncingViewportRef.current = false;
    };
  }, [dockMode, dockRect, canvasSize.width, canvasSize.height, syncToViewport]);

  if (!input || !input.canvasId) return null;

  if (isResizing) {
    return <LoadingSpinner/>
  }

  return (
    <TransformWrapper
      ref={transformRef}
      // rwKey only changes on a true portrait<->landscape flip (see
      // useResizingLoaderHook). It intentionally does NOT change on mount or on
      // ordinary resizes, so the canvas no longer remounts/blinks on load.
      key={rwKey}
      initialScale={dockMode === "embedded" ? minScale : 1}
      // Mount already cover-framed instead of at rzpp's default 0/0 — see initialTransform above.
      // rzpp reads these only in createState (i.e. once per mounted instance), so they matter on
      // the very first mount and again on every rwKey remount, and are inert in between.
      initialPositionX={initialTransform.positionX}
      initialPositionY={initialTransform.positionY}
      minScale={minScale}
      maxScale={3}
      centerOnInit
      centerZoomedOut
      limitToBounds
      alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
      doubleClick={{ disabled: true }}
      wheel={{ step: 0.1 }}
      panning={{ velocityDisabled: true }}
      // Belt-and-suspenders alongside ImageCanvasVisualizer's own drag-threshold
      // tracking: rzpp is the authority on whether a pan actually happened, so
      // flag it here too — this covers a pan that ends with the pointer release
      // outside the canvas element, which the canvas's own onMouseUp would miss.
      // IMPORTANT: onPanningStart fires on raw mousedown, before any movement,
      // so it must NOT set the flag by itself — that made every plain click look
      // like a drag. Instead we record the start point here and only flag a
      // drag once onPanning reports real movement past DRAG_THRESHOLD_PX,
      // mirroring the canvas's own threshold check.
      onPanningStart={(_ref, event) => {
        panStartPointRef.current = getEventPoint(event);
      }}
      onPanning={(_ref, event) => {
        const start = panStartPointRef.current;
        if (!start) return;
        const { x, y } = getEventPoint(event);
        if (Math.hypot(x - start.x, y - start.y) > DRAG_THRESHOLD_PX) {
          (window as any).canvasWasDragged = true;
        }
      }}
      onPanningStop={() => {
        panStartPointRef.current = null;
      }}
    >
      {(utils) => (
        <React.Fragment>
          <TransformComponent
            wrapperStyle={{ width: "100%", height: "100%" }}
            contentStyle={{ width: canvasSize.width, height: canvasSize.height }}
          >
            <ImageCanvasVisualizer
              {...input}
              getRzpp={getRzpp}
              isTouch={isTouch}
              canvasDisplay={canvasSize}
            />
          </TransformComponent>
          <ZoomPanel utils={utils} />
        </React.Fragment>
      )}
    </TransformWrapper>
  );
}
